import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import { CLAIMANT, INDIAN_STATES } from "app/constants";
import { triggerOrixEmail } from "Clients/OrixIndia/API/service";
import SearchAutocomplete from "Components/SearchAutoComplete";
import FormattedMessage from "features/common/TranslatedMessage";
import { introFormContext } from "features/intro/IntroContainer";
import { useFormik } from "formik";
import { useMuthootRegions } from "hooks";
import useLoanTypes from "hooks/useLoanTypes";
import { Steps } from "intro.js-react";
import "intro.js/introjs.css";
import debounce from "lodash/debounce";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  isCDR,
  isDev,
  isDigipub,
  isHotStar,
  isNbf,
  isNSE,
  isViacom,
  isVikalp,
  isWebnyay,
  isStar,
  isMuthoot,
  isOrix,
} from "Routes/Routes";
import { DisputeSchema, HotstarDisputeSchema, MuthootDisputeSchema } from "schemas/DisputeSchema";
import { createCase } from "slices/casesSlice";
import { getCompanyProducts } from "slices/companySlice";
import { createDispute, updateDispute } from "slices/disputeSlice";
import { getCitiesByStateName } from "slices/userSlice";


const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  formContainer: {
    padding: "2rem",
  },
  hoverButton: {
    background: "#3F51B5",
    color: "#FFFFFF",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#3F51B5",
      border: "1px solid #3F51B5",
    },
  },
}));

const IntroForm = ({ profile, user, companies }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { regions } = useMuthootRegions();
  const { loanTypes } = useLoanTypes(isMuthoot);
  const caseLoading = useSelector((state) => state.cases.loading);
  const disputeLoading = useSelector((state) => state.disputes.loading);
  const previousFormData = location?.state?.formData;
  const disputeId = location?.state?.disputeId;

  const { isVendor, setFormStep, setClaimantFormValues } = useContext(introFormContext);
  const [phoneError, setPhoneError] = useState();
  const [companyProducts, setCompanyProducts] = useState([]);
  const [cities, setCities] = useState([]);
  const [complainCategory, setComplainCategory] = useState('');

  const isTempEmail = user?.email && user?.email.startsWith("muthoot_temp_email");
  const [errors, setErrors] = useState(null);

  const grievanceTypes = [
    {
      id: 'query',
      value: 'Query',
    },
    {
      id: 'request',
      value: 'Request',
    },
    {
      id: 'complaint',
      value: 'Complaint',
    },
  ]

  useEffect(() => {
    (isVendor || isOrix || previousFormData?.company_product) &&
      getCompanyProducts(profile.company || companies[0]?.id).then((response) => {
        setCompanyProducts(response);
        if(isOrix){
          formik.setFieldValue("company_product", response[0].id);
          formik.setFieldValue("company_product_type", response[0].id);
        } else if (previousFormData?.company_product) {
          formik.setFieldValue("company", profile?.company || companies[0]?.id)
          formik.setFieldValue("company_product", previousFormData.company_product);
          formik.setFieldValue("company_product_type", response.find(item => item.id === previousFormData.company_product)?.type);
        }
      });
    validatePhone(profile.phone_number);
  }, [previousFormData?.company_product]);

  const onPhoneChange = (e) => {
    const numericValue = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneError();
    validatePhone(numericValue);
    formik.setFieldValue("phone", numericValue);
  };

  const validatePhone = (v) => {
    let ph = v;
    if (ph != null && ph.trim() != "" && (ph.length != 10 || isNaN(ph))) {
      setPhoneError("Invalid phone number");
    }
  };

  const navigateToNextPage = (disputeId, values) => {
    // add the form data to state
    history.replace({
      pathname: location.pathname,
      state: {formData: values}
    });
    // add the form data to history state and navigate to dispute page
    history.push({
      pathname: `/disputes/${disputeId}`,
      state: {formData: values}
    });
  }

  // debounced submit handler for general user as claimant
  const debouncedOnSubmit = debounce((values) => {
    if(phoneError || !formik.isValid){
      return;
    }
    if(!values.existingCustomer){
      history.push('/annexure1');
      triggerOrixEmail('annexure1');
      return;
    }
    if(!values.helpDeskComplainRaised){
      history.push('/annexure2');
      triggerOrixEmail('annexure2');
      return;
    }
    if(isOrix && (complainCategory === 'UNKNOWN')){
      setErrors({
        loanNumber: 'Invalid format of loan number/booking number/schedule number/disbursement number'
      })
      return;
    }
    let company = companies.find((element) => element.id === values.company);
    let name = "";
    if (isHotStar || isStar || isMuthoot || isOrix)
      name = user.first_name + " " + user.last_name + " & " + company.name;
    else name = user.first_name + " " + user.last_name + " vs " + company.name;
    let description = name;
    let disputeBody;
    if (isNbf) {
      disputeBody = {
        name: name,
        company_product: values.company_product || null,
        description: description,
        level_of_dispute: "one",
        claimant_status: "new",
        platform_type:
          isHotStar || isStar || isMuthoot || isWebnyay || isViacom || isDigipub
            ? "grievance"
            : isCDR || isVikalp
            ? "cdr"
            : isNbf
            ? "nbf"
            : "rv",
      };
    } else if (isOrix) {
      disputeBody = {
        "company_product": values.company_product || null,
        "description": description,
        "grievance_type": "complaint",
        "name": name,
        "customer_type": complainCategory,
        "loan_or_booking_number": values.loanNumber,
        "request_ticket_number": values.ticketNumber,
        "platform_type": "grievance",
        "state": values.state,
        "city": values.city,
        "business_verticle": orixBusinessChoices.find(choice => choice.value === values.businessVertical)?.label,
      }
    } else {
      disputeBody = {
        name: name,
        company_product: (isMuthoot ? values.company_product_type : values.company_product) || null,
        business_verticle: values.company_product,
        description: description,
        grievance_type: values.type,
        customer_type: values?.loanType,
        source_of_complaint: values?.sourceOfComplaint,
        branch: values.branch,
        city: isMuthoot ?`${values.branch.br_code} - ${values.branch.branch_name} - ${values.branch.district}` : '',
        created_by: profile.id,
        platform_type:
          isHotStar || isStar || isMuthoot || isWebnyay || isViacom || isDigipub || isNSE
            ? "grievance"
            : isCDR || isVikalp
            ? "cdr"
            : isNbf
            ? "nbf"
            : "rv",
      };
    }

    let createCaseBody = {
      summary: description,
      description: description,
      profile: profile.id,
      type: CLAIMANT,
      email: values.email,
    };

    if (disputeId) {
      dispatch(updateDispute({id: disputeId, ...disputeBody}))
      .then((response) => {
        navigateToNextPage(disputeId, values);
      })
      .catch((err) => {
        console.log(err);
      });
    } else {
      dispatch(createDispute(disputeBody))
      .then((response) => {
        createCaseBody.dispute = response;
        return dispatch(createCase(createCaseBody));
      })
      .then((response) => {
        let respondentCaseBody = {
          summary: createCaseBody.summary,
          description: createCaseBody.description,
          type: "respondent",
          dispute: createCaseBody.dispute,
          region: values.region,
        };

        return dispatch(createCase(respondentCaseBody));
      })
      .then((response) => {
        navigateToNextPage(response.dispute, values);
      })

      .catch((err) => {
        console.log(err);
      });
    }
  }, 1000);

  // handler for vendor as claimant
  const onProceed = (values) => {
    setClaimantFormValues({ ...values });
    setFormStep(2);
  };

  const formik = useFormik({
    initialValues: {
      email: isTempEmail ? "" : user.email,
      phone: profile.phone_number,
      birth_date: new Date(profile.birth_date),
      company: isVendor ? profile.company : isOrix ? companies[0]?.id : '',
      company_product_type: previousFormData?.company_product_type || '',
      company_product: previousFormData?.company_product || '',
      region: previousFormData?.region || "",
      type: previousFormData?.type || null,
      city: previousFormData?.city || null,
      state: previousFormData?.state || null,
      existingCustomer: previousFormData?.existingCustomer || true,
      loanNumber: previousFormData?.loanNumber || '',
      organizationName: previousFormData?.organizationName || '',
      helpDeskComplainRaised: previousFormData?.helpDeskComplainRaised || true,
      ticketNumber: previousFormData?.ticketNumber || '',
      businessVertical: previousFormData?.businessVertical || '',
      loanType: previousFormData?.loanType || '',
      sourceOfComplaint: previousFormData?.sourceOfComplaint || '',
      branch: "",
    },
    onSubmit: isVendor ? onProceed : debouncedOnSubmit,
    validationSchema: (isHotStar || isStar || isOrix) ? HotstarDisputeSchema : isMuthoot ? MuthootDisputeSchema : DisputeSchema,
  });

  const [showGuide, setShowGuide] = useState(
    location.pathname.split("/")[1] == "intro" &&
      (user.id == 473 || user.id == 214) &&
      !isDev
  );

  const exit = (t) => {
    setShowGuide(false);
  };

  const steps = [
    {
      element: ".introstep1",
      intro: "Select the company you wish to file a grievance against.",
    },
    // {
    //   element: ".introstep2",
    //   intro: "Fill some answers.",
    // },
    // {
    //   element: ".introstep3",
    //   intro: "Upload any necessary documents.",
    // },
    // {
    //   element: ".introstep4",
    //   intro: "Review your grievance details",
    // },
    // {
    //   element: ".introstep5",
    //   intro: "Download your automatically generated grievance here!",
    // },
  ];

  const booleanDropdown = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
  ];

  const categorizeInput = (input, businessVertical) => {
    const olfsPrefixes = [
      /^F\/DISB0\w{5}$/, // Matches F/DISB0XXXXX
      /^F\/TRF\w{5}$/,  // Matches F/TRFXXXXX
      /^DMFL020000\w{5}$/, // Matches DMFL020000XXXXX
      /^TRFL020000\w{5}$/, // Matches TRFL020000XXXXX
      /^EXFL020000\w{5}$/,  // Matches EXFL020000XXXXX
      /^LN00000000\w{5}$/, // Matches LN00000000XXXXX
    ];
  
    const ocilPrefixes = [
      /^DISB0\w{5}$/, // Matches DISB0XXXXX
      /^TROL020000\w{5}$/, // Matches TROL020000XXXXX
      /^DMOL020000\w{5}$/, // Matches DMOL020000XXXXX
      /^EXOL020000\w{5}$/  // Matches EXOL020000XXXXX
    ];
  
    const checkByInputText = (inputText) => {
      for (const pattern of olfsPrefixes) {
        if (pattern.test(inputText)) {
          return 'OLFS';
        }
      }
    
      for (const pattern of ocilPrefixes) {
        if (pattern.test(inputText)) {
          return 'OCIL';
        }
      }

      return 'UNKNOWN';
    }

    switch (businessVertical) {
      case 'loan_against_property':
      case 'commercial_vehicle_loan':
      case 'corporate_loan':
      case 'equipment_loan':
      case 'any_other_loan':
        return 'OLFS';

      case 'Mychoize':
      case 'BTS':
      case 'rent_a_car':
        return 'OCIL';

      case 'lease_business':
      case 'finance_lease':
      case 'operating_lease':
      default:
        return checkByInputText(input);
    }

  }

  const orixBusinessChoices = [
    {
      label: "Lease Business",
      value: 'lease_business',
    },
    {
      label: "Finance Lease",
      value: 'finance_lease',
    },
    {
      label: "Loan Against Property",
      value: 'loan_against_property',
    },
    {
      label: "Commercial Vehicle Loan",
      value: 'commercial_vehicle_loan',
    },
    {
      label: "Corporate Loan",
      value: 'corporate_loan',
    },
    {
      label: "Equipment Loan",
      value: 'equipment_loan',
    },
    {
      label: "Any Other Loan",
      value: 'any_other_loan',
    },
    {
      label: "Operating Lease",
      value: 'operating_lease',
    },
    {
      label: "Mychoize",
      value: 'Mychoize',
    },
    {
      label: "BTS",
      value: 'BTS',
    },
    {
      label: "Rent A Car",
      value: 'rent_a_car',
    },
  ];

  const sourceOfComplaints = [
    "Customer",
    "Call Center",
    "Regional Officer",
    "Email",
    "Phone",
  ]

  const fieldRegistry = {
    name: (
      <TextField
        key="name"
        fullWidth
        variant="outlined"
        label="Full Name"
        disabled
        value={`${user.first_name} ${user.last_name}`}
      />
    ),
    email: (
      <TextField
        required = {!isMuthoot}
        id="email"
        name="email"
        label={<FormattedMessage id="user.email" />}
        variant="outlined"
        fullWidth={true}
        className={classes.item}
        onChange={formik.handleChange}
        value={formik.values.email}
        disabled={isMuthoot && !isTempEmail}
      />
    ),
    phone: (
      <>
       <TextField
          required={!(isHotStar || isStar)}
          name="phone"
          id="phone"
          label={<FormattedMessage id="user.phoneNumber" />}
          variant="outlined"
          type="phone"
          fullWidth
          className={classes.item}
          onChange={
            isHotStar || isStar || isMuthoot || isOrix
              ? onPhoneChange
              : formik.handleChange
          }
          value={formik.values.phone}
          inputProps={{ inputMode: 'numeric' }}
          disabled={isMuthoot}
        />
        {phoneError && <Alert severity="error">{phoneError}</Alert>}
      </>
    ),
    type: (
      <FormControl
        fullWidth
        variant="outlined"
        data-testid="type-select"
      >
        <InputLabel id="type">
          Type 
        </InputLabel>
        <Select
          required
          id="type"
          name="type"
          value={formik.values.type}
          onChange={formik.handleChange}
          labelId="type"
          label="Type"
        >
          {grievanceTypes.map((type, index) => (
            <MenuItem key={index} value={type.id}>
              {type.value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
    company: (
      <FormControl
        fullWidth
        variant="outlined"
        data-testid="respondent-select"
        className="introstep1"
      >
        <InputLabel id="against">
          {(isHotStar || isStar || isMuthoot) ? "Platform" : "Respondent"}
        </InputLabel>
        <Select
          id="company"
          name="company"
          value={formik.values.company}
          onChange={(e) => {
            formik.setFieldValue("company_product", null);
            formik.setFieldValue(
              "company_product_type",
              null
            );
            formik.handleChange(e);
            setCompanyProducts([]);
            getCompanyProducts(e.target.value).then(
              (response) => {
                setCompanyProducts(response);
              }
            );
          }}
          labelId="against"
          label={(isHotStar || isStar || isMuthoot) ? "Platform" : "Respondent"}
        >
          {companies.map((company) => {
            if (!company.deleted)
              return (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              );
            return null
          })}
        </Select>
      </FormControl>
    ),
    loan_type: (
      <FormControl
        fullWidth
        variant="outlined"
        data-testid="loanType-select"
      >
        <InputLabel id="against">
          <FormattedMessage id="user.loanType" /> 
        </InputLabel>
        <Select
          required
          id="loanType"
          name="loanType"
          value={formik.values.loanType}
          onChange={(e) => {
            formik.handleChange(e);
            if(e.target.value === "Personal Loan"){
              formik.setFieldValue("branch", {br_code: "", branch_name: "PL/7120", district: "", region: "PL/7120"});
              formik.setFieldValue("region", "PL/7120")
            } else {
              formik.setFieldValue("branch", "");
            }
          }}
          labelId="against"
          label="Loan Type"
        >
          {loanTypes.map((type, index) => (
            <MenuItem key={index+100} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
    branch: (
      <SearchAutocomplete
        required 
        disabled={formik.values.loanType === "Personal Loan"}
        value={formik.values.branch}
        onChange={(e, value) => {
          formik.setFieldValue("branch", value);
          formik.setFieldValue("region", value?.region.toUpperCase() || "");
        }}
      />
    ),
    region: (
      <FormControl fullWidth variant="outlined" data-testid="region-select" disabled={(formik.values.loanType === "Personal Loan") || Boolean(formik.values.branch?.region)}>
        <InputLabel id="region-label" shrink={Boolean(formik.values.region)}>
          <FormattedMessage id="user.region" /> 
        </InputLabel>
        <Select
          required
          id="region"
          name="region"
          value={formik.values.region}
          onChange={formik.handleChange}
          labelId="region-label"
          label="Region"
        >
          {((formik.values.loanType === "Personal Loan") ? ["PL/7120"] : regions)?.map((state, index) => (
            <MenuItem key={index+100} value={state}>{state}</MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
    company_product_type: (
      <FormControl
        fullWidth
        variant="outlined"
        disabled={companyProducts.length === 0}
        data-testid="company-product-select"
      >
        <InputLabel id="against">
          {isMuthoot ? 'Complaint Category' : <FormattedMessage id="dispute.nature" />} 
        </InputLabel>
        <Select
          id="company_product_type"
          name="company_product_type"
          value={formik.values.company_product_type}
          onChange={(e) => {
            formik.setFieldValue("company_product", null);
            formik.handleChange(e);
          }}
          labelId="company_product_type"
          label={isMuthoot ? 'Complaint Category' : 'Company Product Type' }
        >
          {isMuthoot ? (
            (companyProducts?.category || [])?.map((cat, index) => (
              <MenuItem key={index} value={cat.id || cat.name}>
                {cat.name}
              </MenuItem>
            ))
          ) : (
            companyProducts
              .map((item) => item.type)
              .filter(
                (value, index, self) =>
                  self.indexOf(value) === index
              )
              .map((type, index) => (
                <MenuItem key={index} value={type}>
                  {type.replaceAll('_',' ')}
                </MenuItem>
              ))
          )}
        </Select>
      </FormControl>
    ),
    company_product: (
      <FormControl
        fullWidth
        variant="outlined"
        disabled={companyProducts.length === 0}
      >
        <InputLabel id="against">
          {(isHotStar || isStar) ? (
            <FormattedMessage id="dispute.format" />
          ) : (
            isMuthoot ? 'Sub-category' : <FormattedMessage id="dispute.category" />
          )}
        </InputLabel>
        <Select
          id="company_product"
          name="company_product"
          value={formik.values.company_product}
          onChange={formik.handleChange}
          labelId="company_product"
          label={isMuthoot ? 'Sub-category' : 'Company Product'}
          data-testid="company-product-type-select"
        >
          {isMuthoot ? (
            (companyProducts?.['sub_category'] || [])?.map((subCat, index) => (<MenuItem key={index} value={subCat}>{subCat}</MenuItem>))
          ) : (
            companyProducts
              .filter((product) => product.type === formik.values.company_product_type)
              .map((product, index) => (
                <MenuItem
                  key={product.id}
                  value={product.id}
                  data-testid={`company-product-type-item-${index}`}
                  style={{ whiteSpace: "break-spaces" }}
                >
                  {product.name}
                </MenuItem>
              ))
            )}
        </Select>
      </FormControl>
    ),
    source_of_complaint: (
      <FormControl fullWidth variant="outlined" data-testid="sourceOfComplaint-select">
        <InputLabel id="against">
          <FormattedMessage id="user.sourceOfComplaint" /> 
        </InputLabel>
        <Select
          required
          id="sourceOfComplaint"
          name="sourceOfComplaint"
          value={formik.values.sourceOfComplaint}
          onChange={formik.handleChange}
          labelId="against"
          label="Source of Complaint"
        >
          {sourceOfComplaints.map((type, index) => (
            <MenuItem key={index+100} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
    state: (
      <TextField
        required
        fullWidth
        variant='outlined'
        select
        label='State'
        name="state"
        value={formik.values.state}
        onChange={(e) => {
          formik.handleChange(e);
          setCities([]);
          formik.setFieldValue("city", "");
          getCitiesByStateName(e.target.value).then(
            (response) => {
              setCities(response);
            }
          );
        }}
      >
        {INDIAN_STATES.map((state, index) => (
          <MenuItem key={index} value={state}>
            {state}
          </MenuItem>
        ))}
      </TextField>
    ),
    city: (
      <TextField
        required
        fullWidth
        variant='outlined'
        select
        label='City'
        name="city"
        value={formik.values.city}
        onChange={formik.handleChange}
        disabled={cities?.length === 0}
      >
        {cities?.map((city, index) => (
          <MenuItem key={index} value={city}>
            {city}
          </MenuItem>
        ))}
      </TextField>
    ),
    orix_specific_fields: (
      <>
        <TextField
          required
          fullWidth
          variant='outlined'
          select
          label='Are you an existing customer of ORIX India?'
          name="existingCustomer"
          value={formik.values.existingCustomer}
          onChange={formik.handleChange}
        >
          {booleanDropdown?.map((boolean, index) => (
            <MenuItem key={index} value={boolean.value}>
              {boolean.label}
            </MenuItem>
          ))}
        </TextField>
        {formik.values?.existingCustomer && (
          <>
            <TextField
              required
              fullWidth
              variant='outlined'
              select
              label='Which business vertical are you a customer of ORIX India?'
              name="businessVertical"
              value={formik.values.businessVertical}
              onChange={(e)=>{
                formik.handleChange(e);
                setComplainCategory(categorizeInput(formik.values.loanNumber, e.target.value));
              }}
            >
              {orixBusinessChoices?.map((business, index) => (
                <MenuItem key={index} value={business.value}>
                  {business.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="outlined"
              value={formik.values.loanNumber}
              required
              fullWidth
              id="loanNumber"
              label={'Loan Number/Booking Number/Schedule Number/Disbursement Number'}
              name="loanNumber"
              autoComplete="loanNumber"
              onBlur={formik.handleBlur}
              onChange={(e)=>{
                formik.handleChange(e);
                setErrors({loanNumber: ''})
                setComplainCategory(categorizeInput(e.target.value, formik.values.businessVertical));
              }}
              InputProps={{
                endAdornment: (complainCategory && formik.values.loanNumber.trim()) && (
                  <Typography style={{backgroundColor:'#a8f7e3b0',padding:'2px 5px',borderRadius:'3px',lineHeight:'normal'}}>
                    {complainCategory}
                  </Typography>
                )
              }}
              error={errors?.loanNumber}
              helperText={errors?.loanNumber}
            />
            {formik.values.businessVertical === 'lease_business' && (
              <>
                <TextField
                  variant="outlined"
                  value={formik.values.organizationName}
                  fullWidth
                  id="organizationName"
                  label={'Your Organization Name'}
                  name="organizationName"
                  autoComplete="organizationName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </>
            )}
            <TextField
              required
              fullWidth
              variant='outlined'
              select
              label='Have you already raised your concern to ORIX India customer service desk?'
              name="helpDeskComplainRaised"
              value={formik.values.helpDeskComplainRaised}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
            >
              {booleanDropdown?.map((boolean, index) => (
                <MenuItem key={index} value={boolean.value}>
                  {boolean.label}
                </MenuItem>
              ))}
            </TextField>
            {formik.values?.helpDeskComplainRaised && (
              <>
                <TextField
                  required
                  variant="outlined"
                  value={formik.values.ticketNumber}
                  fullWidth
                  id="ticketNumber"
                  label={'Request Ticket Number'}
                  name="ticketNumber"
                  autoComplete="ticketNumber"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </>
            )}
          </>
        )}                 
      </>
    )
  };

  const getFieldOrder = () => {
    if (isMuthoot) {
      return [ 
        "phone", 
        "email", 
        "type", 
        "company", 
        "loan_type", 
        "branch",
        "region", 
        "source_of_complaint",
        "company_product_type", 
        "company_product"
      ];
    }
    if (isOrix) {
      return ["email", "phone", 'state', 'city', 'orix_specific_fields']; 
    }
    if (isVendor) {
      return [
        "email",
        "phone",
        "company_product_type", 
        "company_product",       
      ];
    }

    // Default Order (Existing order)
    return ["email", "phone", "company", "company_product_type", "company_product"];
  };

  return (
    // <Box p="2rem 0" style={{display:"flex",flexDirection:"column",alignItems:"flex-start",border:"1px solid"}}>
    <Grid
      container
      style={{ padding: "2rem 0", display: "flex", justifyContent: "center" }}
    >
      <Grid item xs={10} sm={10}>
        <Steps
          enabled={showGuide}
          steps={steps}
          initialStep={0}
          onExit={exit}
          options={{
            hideNext: false,
            hidePrev: true,
            keyboardNavigation: true,
            exitOnEsc: true,
            exitOnOverlayClick: false,
            showBullets: false,
            showStepNumbers: false,
            disableInteraction: true,
            doneLabel: "Select here",
          }}
        />
      </Grid>
      <Grid item xs={10} sm={10}>
        <Grid container>
          <Grid item md={2} sm={0} xs={0}></Grid>
          <Grid item md={8} sm={12} xs={12}>
            <Paper className={classes.formContainer}>
              {" "}
              <Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  align="center"
                  // onClick={testFunc}
                >
                  <FormattedMessage id="pages.intro.introText" />
                </Typography>
                <Box height="1rem"></Box>
                <Box p="0rem 2rem">
                  <form onSubmit={formik.handleSubmit} data-testid="intro-form" style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
                    {getFieldOrder().map((fieldKey) => (
                      <Fragment key={fieldKey}>
                        {fieldRegistry[fieldKey]}
                        {formik.errors[fieldKey] && formik.touched[fieldKey] && (
                          <Alert severity="error">{formik.errors[fieldKey]}</Alert>
                        )}
                      </Fragment>
                    ))}

                    <Box height="1rem"></Box>
                    <Box display="flex" justifyContent="center">
                      {caseLoading || disputeLoading ? (
                        <CircularProgress />
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          data-testid="submit-intro-claimant"
                          disabled={caseLoading || disputeLoading}
                          className={(isHotStar || isStar || isMuthoot || isOrix) && classes.hoverButton}
                          onClick={()=>console.log('error',formik.errors)}
                        >
                          {isVendor ? (
                            <FormattedMessage id="buttons.proceed" />
                          ) : (
                            <FormattedMessage id="buttons.submit" />
                          )}
                        </Button>
                      )}
                    </Box>
                  </form>
                </Box>
              </Box>
            </Paper>
          </Grid>
          <Grid item md={2} sm={0} xs={0} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default IntroForm;
